import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { ethers } from "ethers";
import { getWCProvider, bringWalletToFront, pingWCSession, purgeWCSession } from "../utils/walletConnect";
import { getAddChainParams } from "../utils/chainParams";
import blockchainApi from "../api/blockchainApi";
import PromosApi from "../api/PromosApi";
import TokenIcon from "../components/TokenIcon";
import TxProgressOverlay from "../components/TxProgressOverlay/TxProgressOverlay";
import { setSelectedPromo } from "../store/slices/selectedPromoSlice";
import { setPage } from "../store/slices/pageSlice";

const RPC_PROXY = 'https://api.duotetra.quest/promos/blockchain/rpc';

const POOL_ABI = [
    'function promos(uint256) view returns (address promoter, address tokenAddress, uint256 totalBudget, uint256 remainingBudget, uint256 endTime, bool active)',
    'function claimGracePeriod() view returns (uint256)',
    'function withdrawUnspent(uint256 promoId)',
];

const ERC20_ABI = [
    'function decimals() view returns (uint8)',
    'function symbol() view returns (string)',
];

const short = (addr) => addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : '';

const fmtAmount = (raw, decimals) => {
    try {
        return ethers.formatUnits(raw, decimals).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
    } catch { return '0'; }
};

const fmtCountdown = (unlockTs) => {
    const s = unlockTs - Math.floor(Date.now() / 1000);
    if (s <= 0) return '';
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
};

export default function WithdrawLayout() {
    const dispatch = useDispatch();
    const [cfg, setCfg] = useState(null);
    const [rows, setRows] = useState(null); // null — грузим
    const [txStatus, setTxStatus] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const lastTxActionRef = useRef(null);

    const load = async () => {
        try {
            const cfgRes = await blockchainApi.getConfig();
            const config = cfgRes?.data || cfgRes;
            setCfg(config);

            const resp = await PromosApi.UserPromos.myPromos();
            const list = Array.isArray(resp?.data?.data) ? resp.data.data : [];
            const onchain = list.filter(p => p.depositTxHash);
            if (!config?.giveawayPool || !onchain.length) { setRows([]); return; }

            const provider = new ethers.JsonRpcProvider(RPC_PROXY);
            const pool = new ethers.Contract(config.giveawayPool, POOL_ABI, provider);
            const grace = await pool.claimGracePeriod();
            const now = Math.floor(Date.now() / 1000);

            const loaded = await Promise.all(onchain.map(async (p) => {
                try {
                    const c = await pool.promos(p.id);
                    if (!c || c.promoter === ethers.ZeroAddress) return null;

                    let decimals = 18, symbol = '';
                    try {
                        const erc = new ethers.Contract(c.tokenAddress, ERC20_ABI, provider);
                        [decimals, symbol] = await Promise.all([
                            erc.decimals().then(Number),
                            erc.symbol(),
                        ]);
                    } catch {}

                    const remaining = c.remainingBudget;
                    const unlockTs = Number(c.endTime + grace);
                    let state;
                    if (remaining === 0n) state = c.active ? 'empty' : 'withdrawn';
                    else if (!c.active) state = 'frozen';
                    else if (now <= unlockTs) state = 'locked';
                    else state = 'ready';

                    return {
                        promo: p,
                        state,
                        promoter: c.promoter,
                        remainingRaw: remaining.toString(),
                        remainingHuman: fmtAmount(remaining, decimals),
                        totalHuman: fmtAmount(c.totalBudget, decimals),
                        symbol,
                        tokenAddress: c.tokenAddress,
                        unlockTs,
                    };
                } catch { return null; }
            }));
            setRows(loaded.filter(Boolean));
        } catch (err) {
            console.error('[Withdraw] load failed:', err);
            setRows([]);
        }
    };

    useEffect(() => { load(); }, []);

    // Своё промо: завершённое/активное — таблица результатов, pending — страница промо
    const openPromo = (p) => {
        dispatch(setSelectedPromo(p));
        const toStats = p.status === 'COMPLETED' || p.status === 'MANUALLY_COMPLETED' || p.status === 'ACTIVE';
        dispatch(setPage(toStats ? { id: 8, name: 'CompletedPromo' } : { id: 5, name: 'PromoDetails' }));
    };

    // ── те же хелперы, что в DepositLayout ──
    const getSigner = async () => {
        const withTimeout = (promise, ms, msg) =>
            Promise.race([promise, new Promise((_, rej) => setTimeout(() => rej(new Error(msg)), ms))]);

        if (window.ethereum) {
            const provider = new ethers.BrowserProvider(window.ethereum);
            await withTimeout(
                provider.send('eth_requestAccounts', []),
                15000, 'MetaMask не ответил. Разблокируй MetaMask и попробуй снова.'
            );
            return provider.getSigner();
        }

        const wc = await withTimeout(
            getWCProvider(),
            10000, 'Не удалось инициализировать WalletConnect'
        );

        if (wc && (wc.connected || wc.session)) {
            const alive = await pingWCSession(undefined, 2000);
            if (alive === false) {
                try { await purgeWCSession(); } catch {}
                throw new Error('Wallet session expired. Reconnect your wallet (From → Change).');
            }
            const provider = new ethers.BrowserProvider(wc);
            return provider.getSigner();
        }

        try {
            await withTimeout(wc.connect(), 180000, 'Кошелёк не ответил на запрос подключения');
            if (wc.connected || wc.session) {
                return new ethers.BrowserProvider(wc).getSigner();
            }
        } catch (e) {
            if (e?.message?.includes('не ответил')) throw e;
        }

        throw new Error('Кошелёк не подключён. Зайди на страницу Wallet и подключи кошелёк.');
    };

    const switchChain = async (provider, chainId) => {
        const hexChainId = '0x' + chainId.toString(16);
        try {
            await Promise.race([
                provider.send('wallet_switchEthereumChain', [{ chainId: hexChainId }]),
                new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 8000)),
            ]);
        } catch (err) {
            if (err?.message === 'timeout') return;
            if (err?.code === 4902 || err?.message?.includes('Unrecognized chain') || err?.message?.includes('wallet_addEthereumChain')) {
                const addParams = getAddChainParams(chainId, RPC_PROXY);
                if (addParams) {
                    try {
                        await provider.send('wallet_addEthereumChain', [addParams]);
                    } catch (_) { /* попробуем отправить транзакцию всё равно */ }
                }
            }
        }
    };

    const txErrorMessage = (err, fallback) => {
        const api = err?.response?.data?.message;
        if (api) return Array.isArray(api) ? api.join(', ') : String(api);
        const deep = err?.info?.error?.message || err?.error?.message;
        if (deep) return deep;
        const msg = err?.shortMessage || err?.reason || err?.message;
        if (msg && !/could not coalesce/i.test(msg)) return msg;
        return msg ? `${fallback} (wallet returned a malformed error)` : fallback;
    };

    const handleWithdraw = async (row) => {
        if (busyId) return;
        lastTxActionRef.current = () => handleWithdraw(row);
        setBusyId(row.promo.id);
        setTxStatus({ type: 'withdraw', step: 'connecting' });
        try {
            const signer = await getSigner();
            if (!cfg?.giveawayPool) throw new Error('Blockchain config not loaded');

            const walletProvider = signer.provider;
            await switchChain(walletProvider, cfg.chainId || 31337);
            const actualChainHex = await walletProvider.send('eth_chainId', []);
            if (cfg.chainId && Number(actualChainHex) !== Number(cfg.chainId)) {
                throw new Error(`Wrong network: switch your wallet to Arbitrum One (now on chain ${Number(actualChainHex)})`);
            }

            // Контракт отдаёт остаток только кошельку, с которого был депозит
            const sender = await signer.getAddress();
            if (sender.toLowerCase() !== String(row.promoter).toLowerCase()) {
                throw new Error(`Use the wallet that funded this promo: ${short(row.promoter)}`);
            }

            setTxStatus({ type: 'withdraw', step: 'tx_sign' });
            const pool = new ethers.Contract(cfg.giveawayPool, POOL_ABI, signer);
            const txReq = pool.withdrawUnspent(row.promo.id);
            setTimeout(bringWalletToFront, 700);
            const tx = await txReq;
            setTxStatus({ type: 'withdraw', step: 'tx_wait' });
            const receipt = await tx.wait();

            // Оптимистичный колбэк — listener всё равно до-синхронизирует
            try {
                await blockchainApi.notifyWithdrawn({
                    promoId: row.promo.id,
                    promoter: sender,
                    amount: row.remainingRaw,
                    txHash: receipt.hash,
                });
            } catch {}

            setTxStatus({ type: 'withdraw', step: 'done' });
            load();
        } catch (err) {
            console.error('Withdraw error:', err);
            setTxStatus(prev => ({
                type: 'withdraw',
                step: prev?.step || 'connecting',
                error: `#${row.promo.id} · ${txErrorMessage(err, 'Withdraw failed')}`,
            }));
        } finally {
            setBusyId(null);
        }
    };

    const StateBadge = ({ row }) => {
        switch (row.state) {
            case 'ready':
                return (
                    <button
                        onClick={() => handleWithdraw(row)}
                        disabled={Boolean(busyId)}
                        className="rounded-[10px] bg-[#3478F6] px-4 py-2 text-[13px] font-semibold text-white transition active:opacity-80 disabled:opacity-50"
                    >
                        Withdraw
                    </button>
                );
            case 'locked':
                return (
                    <div className="flex flex-col items-end">
                        <span className="text-[12px] text-amber-300 font-semibold">Opens in {fmtCountdown(row.unlockTs)}</span>
                        <span className="text-[10px] text-white/35">
                            {new Date(row.unlockTs * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </span>
                    </div>
                );
            case 'frozen':
                return <span className="text-[12px] text-rose-400">Frozen</span>;
            case 'withdrawn':
                return <span className="text-[12px] text-emerald-400">Withdrawn ✓</span>;
            default:
                return <span className="text-[12px] text-white/35">All claimed</span>;
        }
    };

    if (rows === null) {
        return (
            <div className="w-full px-[15px] py-4">
                {[0, 1].map((i) => (
                    <div key={i} className="w-full h-[72px] rounded-[12px] bg-white/5 animate-pulse mb-3" />
                ))}
            </div>
        );
    }

    if (!rows.length) {
        return (
            <div className="w-full h-full flex items-center justify-center px-[15px] py-4">
                <span className="text-white/40 text-[14px]">No on-chain promos yet</span>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col px-[15px] py-4">
            <div className="w-full flex-1 overflow-y-auto">
                {rows.map((row) => (
                    <div
                        key={row.promo.id}
                        className="w-full flex items-center justify-between rounded-[12px] border border-white/10 bg-white/[0.04] px-4 py-3 mb-3"
                    >
                        <div className="flex flex-row items-center gap-3 min-w-0 pr-3">
                            <div className="w-[32px] h-[32px] rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                                <TokenIcon symbol={row.symbol} address={row.tokenAddress} />
                            </div>
                            <div className="flex flex-col gap-0.5 min-w-0">
                                <span className="text-[13px] text-white font-semibold truncate">
                                    {row.state === 'withdrawn'
                                        ? `of ${row.totalHuman} ${row.symbol}`
                                        : `${row.remainingHuman} ${row.symbol} left`}
                                </span>
                                <div className="flex flex-row items-center gap-1.5">
                                    <button
                                        onClick={() => openPromo(row.promo)}
                                        className="rounded-[6px] border border-white/15 px-1.5 py-[1px] text-[10px] text-white/60 transition active:bg-white/10"
                                    >
                                        Promo ›
                                    </button>
                                    {row.state === 'ready' && (
                                        <span className="text-[10px] text-white/35 font-mono truncate">
                                            to {short(row.promoter)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <StateBadge row={row} />
                    </div>
                ))}
            </div>

            <TxProgressOverlay
                status={txStatus}
                onClose={() => setTxStatus(null)}
                onRetry={txStatus?.error && lastTxActionRef.current ? () => {
                    const fn = lastTxActionRef.current;
                    setTxStatus(null);
                    fn?.();
                } : undefined}
            />
        </div>
    );
}
